<script setup>
import { reactive, computed } from "vue";

const emit = defineEmits(["submitted"]);

const form = reactive({
  intent: "order",
  firstName: "",
  email: "",
  phone: "",
  about: "",
});
const notice = reactive({ kind: "", text: "" });

const inputCls =
  "w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-slate-900";
const noteCls = computed(() =>
  notice.kind === "success"
    ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-100"
    : "bg-rose-50 text-rose-800 ring-1 ring-rose-100"
);

const intentOptions = [
  { value: "order", label: "Zamówić stronę internetową" },
  { value: "time", label: "Zapytaj o termin" },
];

const intentHelper = computed(() =>
  form.intent === "order"
    ? "Dopasujemy ofertę i przyspieszymy start projektu. Rabat -10% naliczymy w wycenie."
    : "Oddzwonimy/odpiszemy z najbliższymi wolnymi terminami."
);

const submitLabel = computed(() =>
  form.intent === "order" ? "Zamów stronę" : "Zapytaj o termin"
);

function submitted() {
  emit("submitted", { ...form });
  // Minimalna obsługa wysyłki – do podpięcia pod API / endpoint zapisu.
  notice.kind = "success";
  notice.text =
    form.intent === "order"
      ? "Dziękujemy! Odezwiemy się z dopasowaną ofertą i rabatem -10%."
      : "Dziękujemy! Wrócimy z najbliższymi wolnymi terminami.";
}

defineExpose({ notice });
</script>

<template>
  <!-- modal -->
  <div class="rounded-2xl bg-white ring-1 ring-black/10 overflow-hidden">
    <!-- header / hero -->
    <header
      class="bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white p-6 md:p-7"
    >
      <div class="mt-4 text-center">
        <span
          class="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm ring-1 ring-inset ring-white/20"
        >
          <strong class="font-semibold">–10%</strong>
          <span>na zamówienie z formularza (do 31.12.2025)</span>
        </span>
        <h2 class="mt-4 text-2xl md:text-4xl font-semibold">
          Zamów stronę lub zapytaj o termin
        </h2>
        <p class="mt-3 md:mt-4 text-white/90">
          Wypełnij krótki formularz — naliczymy rabat przy zamówieniu i wrócimy z
          dopasowaną propozycją.
        </p>
      </div>
    </header>

    <!-- form -->
    <form @submit.prevent="handleSubmit" class="p-5 md:p-6">
      <div class="px-4 py-3">
        <h4 class="font-semibold text-gray-900">Dane kontaktowe</h4>
        <p class="text-xs text-gray-500 mt-1">
          Wybierz, czy chcesz już zamówić stronę, czy najpierw sprawdzić wolny termin.
          Oddzwonimy/odpiszemy z terminem i dokładną ofertą.
        </p>
      </div>

      <div v-if="notice.kind" class="px-4 pt-3">
        <div class="rounded-lg px-3 py-2 text-sm" :class="noteCls">
          {{ notice.text }}
        </div>
      </div>

      <div class="px-4 py-4 space-y-3">
        <label class="text-sm block">
          <span class="block mb-1 text-gray-700">Co chcesz zrobić? *</span>
          <select
            v-model="form.intent"
            required
            :class="inputCls"
            aria-label="Wybór celu formularza"
          >
            <option v-for="opt in intentOptions" :key="opt.value" :value="opt.value">
              {{ opt.label }}
            </option>
          </select>
          <span class="mt-1 block text-[11px] text-gray-500">{{ intentHelper }}</span>
        </label>

        <label class="text-sm block">
          <span class="block mb-1 text-gray-700">Imię *</span>
          <input v-model="form.firstName" required :class="inputCls" />
        </label>
        <label class="text-sm block">
          <span class="block mb-1 text-gray-700">E-mail *</span>
          <input v-model="form.email" type="email" required :class="inputCls" />
        </label>
        <label class="text-sm block">
          <span class="block mb-1 text-gray-700">Telefon *</span>
          <input v-model="form.phone" inputmode="tel" required :class="inputCls" />
        </label>
        <label class="text-sm block">
          <span class="block mb-1 text-gray-700"
            >Opowiedz nam o swojej firmie (opcjonalnie)</span
          >
          <textarea v-model="form.about" rows="4" :class="inputCls"></textarea>
        </label>
        <p class="text-[11px] text-gray-500">
          Bez spamu. Dane przetwarzamy wyłącznie w celu przygotowania oferty.
        </p>
      </div>

      <div class="mt-4 flex items-center justify-end gap-3">
        <button
          type="submit"
          class="rounded-lg bg-slate-900 px-5 py-2.5 text-white hover:bg-slate-800 cursor-pointer"
          @click="submitted"
        >
          {{ submitLabel }}
        </button>
      </div>
    </form>
  </div>
</template>
